/**
 * AuthoritativeTextProvider - Single Source of Truth for Document Text
 * 
 * ✅ OPTION A IMPLEMENTATION
 * 
 * This module provides utilities for accessing the authoritative document text.
 * WASM's lastInitializedText is the single source of truth for all text operations.
 * 
 * ALL text reads in the system should go through these utilities.
 * Do NOT read directly from:
 * - documentState?.text (React props - may be stale)
 * - currentDocumentTextRef.current (local ref - may be stale)
 * - optimisticText (local state - may be stale)
 */

import type { TypesetterWorkerClient } from '../../../../workers/TypesetterWorkerClient';

// ============================================================================
// Types
// ============================================================================

export interface AuthoritativeTextState {
  /** The authoritative document text */
  text: string;
  /** Whether the text comes from WASM (true) or fallback (false) */
  isFromWasm: boolean;
  /** Text length for quick access */
  length: number;
  /** Number of form feeds (page breaks) in the text */
  formFeedCount: number;
}

export interface TextAccessorDependencies {
  /** Reference to the TypesetterWorkerClient */
  typesetterClientRef: React.RefObject<TypesetterWorkerClient | null>;
  /** Whether WASM text is ready */
  isWasmTextReady: boolean;
  /** Fallback text for initial load (before WASM ready) */
  fallbackText: string;
}

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Get the authoritative document text.
 * 
 * @param deps - Dependencies for text access
 * @returns The authoritative text from WASM, or fallback if WASM not ready
 */
export function getAuthoritativeText(deps: TextAccessorDependencies): string {
  const { typesetterClientRef, isWasmTextReady, fallbackText } = deps;
  const client = typesetterClientRef.current;
  
  if (client && isWasmTextReady) {
    return client.getLastInitializedText();
  }
  
  // Fallback: WASM not ready yet
  return fallbackText;
}

/**
 * Get the authoritative text state with metadata.
 * Useful for diagnostics and validation.
 * 
 * @param deps - Dependencies for text access
 * @returns Full text state including metadata
 */
export function getAuthoritativeTextState(deps: TextAccessorDependencies): AuthoritativeTextState {
  const text = getAuthoritativeText(deps);
  
  return {
    text,
    isFromWasm: deps.isWasmTextReady,
    length: text.length,
    formFeedCount: (text.match(/\f/g) || []).length
  };
}

/**
 * Get character at a specific grapheme index from authoritative text.
 * 
 * @param deps - Dependencies for text access
 * @param graphemeIndex - The grapheme index to look up
 * @returns The character at that position, or null if out of bounds
 */
export function getCharacterAtPosition(
  deps: TextAccessorDependencies,
  graphemeIndex: number
): string | null {
  const text = getAuthoritativeText(deps);
  
  if (graphemeIndex < 0 || graphemeIndex >= text.length) {
    return null;
  }
  
  return text.charAt(graphemeIndex);
}

/**
 * Get character type at a specific position for diagnostics.
 * 
 * @param char - The character to classify
 * @returns Human-readable character type
 */
export function getCharacterType(char: string | null): string {
  if (char === null) return '(out-of-bounds)';
  if (char === '\f') return 'FORM_FEED';
  if (char === '\n') return 'NEWLINE';
  if (char === ' ') return 'SPACE';
  if (char === '\t') return 'TAB';
  if (char === '\r') return 'CARRIAGE_RETURN';
  return `'${char}'`;
}

/**
 * Validate that a grapheme index is within bounds of the authoritative text.
 * 
 * @param deps - Dependencies for text access
 * @param graphemeIndex - The grapheme index to validate
 * @returns Object with validation result and clamped index
 */
export function validateGraphemeIndex(
  deps: TextAccessorDependencies,
  graphemeIndex: number
): { valid: boolean; clampedIndex: number; textLength: number } {
  const text = getAuthoritativeText(deps);
  const textLength = text.length;
  
  if (graphemeIndex < 0) {
    return { valid: false, clampedIndex: 0, textLength };
  }
  
  if (graphemeIndex > textLength) {
    return { valid: false, clampedIndex: textLength, textLength };
  }
  
  return { valid: true, clampedIndex: graphemeIndex, textLength };
}

// ============================================================================
// Diagnostic Utilities
// ============================================================================

/**
 * Log diagnostic information about the current text state.
 * Use this for debugging text synchronization issues.
 */
export function logTextStateDiagnostics(
  deps: TextAccessorDependencies,
  context: string,
  additionalInfo?: Record<string, unknown>
): void {
  const state = getAuthoritativeTextState(deps);
  
  console.log(`🔬 [TEXT DIAGNOSTIC] ${context}:`, {
    textLength: state.length,
    isFromWasm: state.isFromWasm,
    formFeedCount: state.formFeedCount,
    textPreview: JSON.stringify(state.text.slice(0, 30)),
    ...additionalInfo
  });
}

/**
 * Compare authoritative text with a local text value.
 * Useful for detecting desync issues.
 */
export function compareWithAuthoritativeText(
  deps: TextAccessorDependencies,
  localText: string,
  localTextName: string
): { inSync: boolean; differences: string[] } {
  const authText = getAuthoritativeText(deps);
  const differences: string[] = [];
  
  if (authText.length !== localText.length) {
    differences.push(`Length mismatch: authoritative=${authText.length}, ${localTextName}=${localText.length}`);
  }
  
  const authFormFeeds = (authText.match(/\f/g) || []).length;
  const localFormFeeds = (localText.match(/\f/g) || []).length;
  
  if (authFormFeeds !== localFormFeeds) {
    differences.push(`Form feed count mismatch: authoritative=${authFormFeeds}, ${localTextName}=${localFormFeeds}`);
  }
  
  if (authText !== localText) {
    // Find first difference position
    let firstDiffPos = 0;
    while (firstDiffPos < Math.min(authText.length, localText.length) && 
           authText[firstDiffPos] === localText[firstDiffPos]) {
      firstDiffPos++;
    }
    differences.push(`First difference at position ${firstDiffPos}`);
  }
  
  return {
    inSync: differences.length === 0,
    differences
  };
}
